(function(){
  if ((window.__cfLoaded = window.__cfLoaded || {})['cf-enh-shell']) return;
  window.__cfLoaded['cf-enh-shell'] = true;
function useReveal(opts = {}) {
  const ref = React.useRef(null);
  const optOut = (() => {
    try {
      if (typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
      if (typeof document !== "undefined" && document.documentElement.getAttribute("data-motion") === "off") return true;
    } catch (e) {
    }
    return false;
  })();
  const [seen, setSeen] = React.useState(optOut);
  React.useEffect(() => {
    if (seen) return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setSeen(true);
      if (io) io.disconnect();
      clearTimeout(failTimer);
    };
    let io = null;
    if (ref.current && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            finish();
            break;
          }
        }
      }, { rootMargin: opts.rootMargin || "0px 0px -10% 0px", threshold: opts.threshold || 0.12 });
      io.observe(ref.current);
    }
    const failTimer = setTimeout(finish, 1200);
    return () => {
      done = true;
      if (io) io.disconnect();
      clearTimeout(failTimer);
    };
  }, []);
  return [ref, seen];
}
function Reveal({ as: Tag = "div", kind = "reveal", delay = 0, children, ...rest }) {
  const [ref, seen] = useReveal();
  const cls = `${kind} ${seen ? "in" : ""} ${rest.className || ""}`.trim();
  const style = { ...rest.style || {}, "--rd": `${delay}ms` };
  return /* @__PURE__ */ React.createElement(Tag, { ref, ...rest, className: cls, style }, children);
}
function RisingText({ lines, delay = 0, lineDelay = 90, className = "", style = {} }) {
  const [ref, seen] = useReveal();
  return /* @__PURE__ */ React.createElement("span", { ref, className, style }, lines.map((line, i) => /* @__PURE__ */ React.createElement("span", { key: i, className: "reveal-rise", style: { display: "block" } }, /* @__PURE__ */ React.createElement("span", { style: { transitionDelay: `${delay + i * lineDelay}ms`, transform: seen ? "translateY(0)" : "translateY(110%)" } }, line))));
}
function useScrollProgress() {
  const ref = React.useRef(null);
  const [p, setP] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const h = window.innerHeight || 800;
        const ST = 72;
        const pinH = h - ST;
        const travel = r.height - pinH;
        let p2;
        if (travel > 0) {
          p2 = (ST - r.top) / travel / 0.82;
        } else {
          const total = r.height + h * 0.6;
          p2 = (h * 0.6 - r.top) / total;
        }
        setP(Math.max(0, Math.min(1, p2)));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return [ref, p];
}
function useParallax(amount = 40) {
  const ref = React.useRef(null);
  const [y, setY] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const h = window.innerHeight || 800;
        const centre = r.top + r.height / 2;
        const t = (centre - h / 2) / h;
        setY(-t * amount);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [amount]);
  return [ref, y];
}
function OsContourBg({ seed = "sheffield", density = 14, opacity = 0.18, color = "var(--ink)" }) {
  const lines = React.useMemo(() => {
    const rng = window.makeRng(seed + ":contours");
    const out = [];
    for (let i = 0; i < density; i++) {
      const cy = rng() * 1e3;
      const amp = 30 + rng() * 60;
      const freq = 0.6 + rng() * 1.2;
      const phase = rng() * Math.PI * 2;
      const drift = (rng() - 0.5) * 40;
      let d = `M 0 ${cy}`;
      for (let x = 50; x <= 1600; x += 40) {
        const y = cy + Math.sin(x / 1e3 * Math.PI * freq + phase) * amp + x / 1600 * drift;
        d += ` L ${x} ${y.toFixed(1)}`;
      }
      out.push({ d, w: 0.4 + rng() * 0.4 });
    }
    return out;
  }, [seed, density]);
  return /* @__PURE__ */ React.createElement("div", { className: "contour-bg", style: { opacity } }, /* @__PURE__ */ React.createElement("svg", { viewBox: "0 0 1600 1000", preserveAspectRatio: "xMidYMid slice" }, /* @__PURE__ */ React.createElement("g", { fill: "none", stroke: color }, lines.map((l, i) => /* @__PURE__ */ React.createElement("path", { key: i, d: l.d, strokeWidth: l.w })))));
}
function CornerMarks() {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", { className: "corner-mark tl" }), /* @__PURE__ */ React.createElement("span", { className: "corner-mark tr" }), /* @__PURE__ */ React.createElement("span", { className: "corner-mark bl" }), /* @__PURE__ */ React.createElement("span", { className: "corner-mark br" }));
}
function NorthMark({ size = 36, color = "var(--ink)", opacity = 0.7 }) {
  return /* @__PURE__ */ React.createElement("svg", { viewBox: "0 0 40 56", width: size, height: size * 1.4, style: { opacity } }, /* @__PURE__ */ React.createElement("line", { x1: "20", y1: "6", x2: "20", y2: "50", stroke: color, strokeWidth: "1" }), /* @__PURE__ */ React.createElement("path", { d: "M 20 6 L 26 22 L 20 16 L 14 22 Z", fill: color }), /* @__PURE__ */ React.createElement("text", { x: "20", y: "4", textAnchor: "middle", fontFamily: "Inter", fontSize: "9", letterSpacing: "2", fontWeight: "500", fill: color }, "N"));
}
function TopNav({ route, go, cartCount }) {
  const items = [
    { id: "atlas", label: "Collection" },
    { id: "atelier", label: "Commission" },
    { id: "journal", label: "Journal" },
    { id: "about", label: "Studio" }
  ];
  const [scrolled, setScrolled] = React.useState(false);
  const [liveCount, setLiveCount] = React.useState(cartCount || 0);
  React.useEffect(() => {
    let alive = true;
    if (window.cityformShopify && window.cityformShopify.cartCount) {
      window.cityformShopify.cartCount().then((n) => {
        if (alive) setLiveCount(n);
      }).catch(() => {
      });
    }
    const onChange = (e) => setLiveCount(e.detail ? e.detail.totalQuantity : 0);
    window.addEventListener("cf-cart-changed", onChange);
    return () => {
      alive = false;
      window.removeEventListener("cf-cart-changed", onChange);
    };
  }, []);
  React.useEffect(() => {
    const f = () => setScrolled(window.scrollY > 8);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const results = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return (window.CATALOG || []).filter((c) => ((c.name || "") + " " + (c.region || "")).toLowerCase().includes(s)).slice(0, 8);
  }, [q]);
  const pickResult = (id) => {
    setSearchOpen(false);
    setQ("");
    go("product", id);
  };
  React.useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setQ("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);
  return /* @__PURE__ */ React.createElement("div", { style: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: scrolled ? "rgba(225, 229, 224, 0.92)" : "var(--bg)",
    WebkitBackdropFilter: scrolled ? "blur(14px)" : "none",
    backdropFilter: scrolled ? "blur(14px)" : "none",
    borderBottom: "1px solid var(--line)",
    transition: "background .2s"
  } }, /* @__PURE__ */ React.createElement("div", { className: "container", style: { display: "flex", alignItems: "center", height: 72 } }, /* @__PURE__ */ React.createElement("button", { onClick: () => go("home"), style: { marginRight: 48, display: "flex", alignItems: "center", gap: 14 } }, /* @__PURE__ */ React.createElement(Wordmark, { size: 26 }), /* @__PURE__ */ React.createElement("span", { style: { width: 1, height: 18, background: "var(--line-strong)" } }), /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { fontSize: 9 } }, "EST. SHEFFIELD \xB7 2024")), /* @__PURE__ */ React.createElement("nav", { style: { display: "flex", gap: 36 } }, items.map((it) => /* @__PURE__ */ React.createElement(
    "button",
    {
      key: it.id,
      onClick: () => go(it.id),
      style: {
        color: route === it.id ? "var(--ink)" : "var(--stone)",
        fontWeight: route === it.id ? 600 : 500,
        fontSize: 13.5,
        letterSpacing: "-0.005em",
        position: "relative",
        paddingBottom: 4,
        transition: "color .15s"
      },
      onMouseEnter: (e) => {
        e.currentTarget.style.color = "var(--ink)";
      },
      onMouseLeave: (e) => {
        if (route !== it.id) e.currentTarget.style.color = "var(--stone)";
      }
    },
    it.label,
    route === it.id && /* @__PURE__ */ React.createElement("span", { style: {
      position: "absolute",
      bottom: -2,
      left: 0,
      right: 0,
      height: 1,
      background: "var(--ink)"
    } })
  ))), /* @__PURE__ */ React.createElement("div", { style: { flex: 1 } }), /* @__PURE__ */ React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 24, color: "var(--ink-soft)" } }, /* @__PURE__ */ React.createElement(
    "button",
    {
      title: "Search the collection",
      onClick: () => setSearchOpen((o) => !o),
      style: { display: "flex", alignItems: "center", gap: 8 }
    },
    Icon.search,
    /* @__PURE__ */ React.createElement("span", { style: { fontSize: 12, color: "var(--stone)" } }, "Search the collection")
  ), /* @__PURE__ */ React.createElement("span", { style: { width: 1, height: 18, background: "var(--line-strong)" } }), /* @__PURE__ */ React.createElement(
    "button",
    {
      onClick: () => {
        window.location = window.cityformShopify.cartUrl();
      },
      style: { display: "flex", alignItems: "center", gap: 8 },
      title: "Bag \u2014 opens Shopify cart"
    },
    Icon.bag,
    /* @__PURE__ */ React.createElement("span", { className: "tnum", style: { fontSize: 12, minWidth: 14 } }, String(liveCount).padStart(2, "0"))
  ))), searchOpen && /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 72,
    background: "var(--surface)",
    borderBottom: "1px solid var(--line)",
    boxShadow: "0 12px 28px rgba(26,31,38,0.08)"
  } }, /* @__PURE__ */ React.createElement("div", { className: "container", style: { padding: "20px 32px" } }, /* @__PURE__ */ React.createElement(
    "input",
    {
      autoFocus: true,
      value: q,
      onChange: (e) => setQ(e.target.value),
      placeholder: "A city or area \u2014 Whitby, Liverpool\u2026",
      style: {
        width: "100%",
        height: 44,
        border: 0,
        borderBottom: "1px solid var(--line-strong)",
        background: "transparent",
        color: "var(--ink)",
        fontFamily: "Inter, sans-serif",
        fontSize: 18,
        outline: "none",
        padding: "0 2px"
      }
    }
  ), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 14 } }, q.trim() && results.length === 0 && /* @__PURE__ */ React.createElement("div", { className: "body", style: { fontSize: 13, color: "var(--stone)", padding: "10px 2px" } }, "Nothing in the collection matches \u201C", q.trim(), "\u201D."), results.map((c) => /* @__PURE__ */ React.createElement(
    "button",
    {
      key: c.id,
      onClick: () => pickResult(c.id),
      style: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        width: "100%",
        textAlign: "left",
        padding: "10px 2px",
        borderBottom: "1px solid var(--line)",
        gap: 16
      }
    },
    /* @__PURE__ */ React.createElement("span", { style: { fontSize: 15, fontWeight: 600, color: "var(--ink)" } }, c.name),
    /* @__PURE__ */ React.createElement("span", { className: "caption", style: { fontSize: 11 } }, [c.region, c.stock > 0 ? `\xA3${c.price}` : "Sold"].filter(Boolean).join(" \xB7 "))
  ))))));
}
function Footer({ go }) {
  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);
  const submitSubscribe = () => {
    const e = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(e)) {
      setSubscribed("bad");
      return;
    }
    window.location.href = "mailto:hello@cityform.co.uk?subject=" + encodeURIComponent("Add me to the cityform list") + "&body=" + encodeURIComponent("Please add this address to the cityform list: " + e);
    setSubscribed(true);
  };
  return /* @__PURE__ */ React.createElement("footer", { style: { background: "var(--ink)", color: "var(--mist)", position: "relative", overflow: "hidden" } }, /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    left: 0,
    right: 0,
    top: -30,
    fontFamily: "Inter, sans-serif",
    fontVariationSettings: "'opsz' 32",
    fontWeight: 700,
    letterSpacing: "-0.045em",
    textAlign: "center",
    fontSize: "clamp(280px, 30vw, 480px)",
    lineHeight: 0.85,
    color: "rgba(225,229,224,0.05)",
    textTransform: "lowercase",
    userSelect: "none",
    pointerEvents: "none",
    zIndex: 0
  } }, "cityform"), /* @__PURE__ */ React.createElement("div", { className: "container", style: { paddingTop: 96, paddingBottom: 36, position: "relative", zIndex: 1 } }, /* @__PURE__ */ React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 48, marginBottom: 80 } }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Wordmark, { size: 48, surface: "reverse" }), /* @__PURE__ */ React.createElement("p", { style: { marginTop: 24, maxWidth: 380, fontSize: 14.5, lineHeight: 1.6, color: "rgba(225,229,224,0.72)" } }, "Architecturally considered miniature models of British cities. Cut from the same square kilometre of public survey data that planners use, scaled to nine centimetres on your shelf."), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 32, display: "flex", alignItems: "center", gap: 18 } }, /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { color: "rgba(225,229,224,0.55)", fontSize: 9 } }, "53.3811\xB0 N \xB7 1.4701\xB0 W"), /* @__PURE__ */ React.createElement("span", { style: { width: 1, height: 12, background: "rgba(225,229,224,0.2)" } }), /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { color: "rgba(225,229,224,0.55)", fontSize: 9 } }, "SHEFFIELD STUDIO"))), [
    { h: "Shop", items: [["The collection", "atlas"], ["Now shipping", "atlas"], ["In development", "atlas"], ["Gift cards", "home"]] },
    { h: "Commission", items: [["Start a commission", "atelier"], ["Lead times", "atelier"], ["Sample request", "atelier"]] },
    { h: "Studio", items: [["Process", "about"], ["Data sources", "about"], ["Contact", "about"]] }
  ].map((col) => /* @__PURE__ */ React.createElement("div", { key: col.h }, /* @__PURE__ */ React.createElement("div", { style: { fontSize: 9, letterSpacing: "0.32em", fontWeight: 500, color: "rgba(225,229,224,0.5)", marginBottom: 22 } }, col.h.toUpperCase()), /* @__PURE__ */ React.createElement("ul", { style: { listStyle: "none", padding: 0, margin: 0 } }, col.items.map(([label, r]) => /* @__PURE__ */ React.createElement("li", { key: label, style: { marginBottom: 12 } }, /* @__PURE__ */ React.createElement("button", { onClick: () => go(r), style: { fontSize: 14, color: "rgba(225,229,224,0.85)", textAlign: "left" } }, label))))))), /* @__PURE__ */ React.createElement("div", { style: {
    padding: "32px 0",
    borderTop: "1px solid rgba(225,229,224,0.12)",
    borderBottom: "1px solid rgba(225,229,224,0.12)",
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: 32,
    alignItems: "center"
  } }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { className: "display-section", style: { fontSize: 38, color: "var(--mist)" } }, "A short letter when a new city ships."), /* @__PURE__ */ React.createElement("p", { style: { marginTop: 10, fontSize: 13, color: "rgba(225,229,224,0.6)" } }, "One email a month, on average. Sometimes less.")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { style: { display: "flex", gap: 0, border: "1px solid rgba(225,229,224,0.25)" } }, /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "email",
      placeholder: "your.address@somewhere.co.uk",
      value: email,
      onChange: (e) => {
        setEmail(e.target.value);
        if (subscribed) setSubscribed(false);
      },
      onKeyDown: (e) => {
        if (e.key === "Enter") submitSubscribe();
      },
      style: {
        flex: 1,
        background: "transparent",
        border: 0,
        outline: "none",
        padding: "0 20px",
        height: 52,
        fontFamily: "Inter, sans-serif",
        fontSize: 13.5,
        color: "var(--mist)"
      }
    }
  ), /* @__PURE__ */ React.createElement("button", { onClick: submitSubscribe, style: {
    padding: "0 24px",
    height: 52,
    background: "var(--mist)",
    color: "var(--ink)",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.18em"
  } }, "SUBSCRIBE")), subscribed === true && /* @__PURE__ */ React.createElement("p", { style: { marginTop: 10, fontSize: 12, color: "rgba(225,229,224,0.7)" } }, "Opening your mail app to confirm \u2014 or just email hello@cityform.co.uk."), subscribed === "bad" && /* @__PURE__ */ React.createElement("p", { style: { marginTop: 10, fontSize: 12, color: "var(--coral)" } }, "Enter a valid email address."))), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 36, display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(225,229,224,0.55)", letterSpacing: "0.06em" } }, /* @__PURE__ */ React.createElement("span", null, "\xA9 2026 Cityform \xB7 Sheffield, UK"), /* @__PURE__ */ React.createElement("span", { style: { display: "flex", gap: 18 } }, /* @__PURE__ */ React.createElement(
    "a",
    {
      href: "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/",
      target: "_blank",
      rel: "noopener",
      style: { color: "inherit", textDecoration: "underline", textDecorationColor: "rgba(225,229,224,0.3)" }
    },
    "Contains EA LIDAR (OGL v3.0)"
  ), /* @__PURE__ */ React.createElement(
    "a",
    {
      href: "https://www.openstreetmap.org/copyright",
      target: "_blank",
      rel: "noopener",
      style: { color: "inherit", textDecoration: "underline", textDecorationColor: "rgba(225,229,224,0.3)" }
    },
    "Map data \xA9 OpenStreetMap contributors (ODbL)"
  )))));
}
function ProductCard({ item, onClick, density = "standard", variant = "photo" }) {
  const [hover, setHover] = React.useState(false);
  const photos = item.photos || [item.on_base || item.photo].filter(Boolean);
  const primary = photos[0];
  const secondary = photos[1] || primary;
  return /* @__PURE__ */ React.createElement(
    "button",
    {
      onClick,
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => setHover(false),
      className: "card-lift",
      style: { display: "block", width: "100%", height: "100%", textAlign: "left" }
    },
    /* @__PURE__ */ React.createElement("div", { style: {
      background: "var(--bone)",
      border: "1px solid var(--line)",
      overflow: "hidden",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      transition: "border-color .2s",
      borderColor: hover ? "var(--ink)" : "var(--line)"
    } }, /* @__PURE__ */ React.createElement("div", { style: {
      aspectRatio: "4 / 5",
      flexShrink: 0,
      background: "var(--surface-2)",
      position: "relative",
      overflow: "hidden"
    } }, /* @__PURE__ */ React.createElement(
      "img",
      {
        src: primary,
        alt: item.name,
        className: "img-editorial",
        style: {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: hover && secondary !== primary ? 0 : 1,
          transition: "opacity .35s ease, transform 1s cubic-bezier(.2,.6,.2,1)",
          transform: hover ? "scale(1.03)" : "scale(1)"
        }
      }
    ), secondary !== primary && /* @__PURE__ */ React.createElement(
      "img",
      {
        src: secondary,
        alt: "",
        className: "img-editorial",
        style: {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: hover ? 1 : 0,
          transition: "opacity .35s ease, transform 1s cubic-bezier(.2,.6,.2,1)",
          transform: hover ? "scale(1.03)" : "scale(1.06)"
        }
      }
    ), item.accent && /* @__PURE__ */ React.createElement("div", { style: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `var(--${item.accent})`,
      transformOrigin: "left",
      transform: hover ? "scaleX(1)" : "scaleX(0)",
      transition: "transform .5s cubic-bezier(.2,.6,.2,1)"
    } }), /* @__PURE__ */ React.createElement("div", { style: {
      position: "absolute",
      bottom: 12,
      left: 12,
      padding: "5px 10px",
      background: "var(--bone)",
      border: "1px solid var(--ink)",
      fontSize: 9,
      letterSpacing: "0.18em",
      fontWeight: 500,
      fontVariantNumeric: "tabular-nums"
    } }, item.coords.lat.toFixed(3), "\xB0", item.coords.ns, " \xB7 ", Math.abs(item.coords.lng).toFixed(3), "\xB0", item.coords.ew), /* @__PURE__ */ React.createElement("div", { style: {
      position: "absolute",
      bottom: 12,
      right: 12,
      background: "var(--ink)",
      color: "var(--mist)",
      padding: "6px 12px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      opacity: hover ? 1 : 0,
      transform: hover ? "translateX(0)" : "translateX(8px)",
      transition: "opacity .3s ease, transform .35s cubic-bezier(.2,.6,.2,1)"
    } }, /* @__PURE__ */ React.createElement("span", { style: { fontSize: 10, letterSpacing: "0.22em", fontWeight: 500 } }, "VIEW"), Icon.arrow), item.status === "soon" && /* @__PURE__ */ React.createElement("div", { style: {
      position: "absolute",
      top: 12,
      left: 12,
      background: "var(--ink)",
      color: "var(--mist)",
      padding: "4px 10px",
      fontSize: 9,
      letterSpacing: "0.22em",
      fontWeight: 500
    } }, "IN DEVELOPMENT")), /* @__PURE__ */ React.createElement("div", { style: { padding: density === "dense" ? "14px 16px" : "20px 22px" } }, /* @__PURE__ */ React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 } }, /* @__PURE__ */ React.createElement("div", { className: "h3", style: {
      fontSize: density === "dense" ? 16 : 19,
      fontWeight: 600,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    } }, item.name), /* @__PURE__ */ React.createElement("div", { className: "tnum", style: { fontSize: 12.5, color: "var(--stone)", flexShrink: 0 } }, item.status === "soon" ? "\u2014" : `\xA3${item.price || 95}`)), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 8, display: "flex", alignItems: "center", gap: 8 } }, item.accent ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", { style: { width: 7, height: 7, background: `var(--${item.accent})` } }), /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { fontSize: 9 } }, (item.landmark || item.region).toUpperCase())) : /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { fontSize: 9, color: "var(--stone)" } }, item.region.toUpperCase()))))
  );
}
function AtlasMap({ items, t, go }) {
  const wrapRef = React.useRef(null);
  const [hovered, setHovered] = React.useState(null);
  const [leafletReady, setLeafletReady] = React.useState(!!window.L);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const goRef = React.useRef(go);
  goRef.current = go;
  React.useEffect(() => {
    if (leafletReady) return;
    let t2 = 0;
    const id = setInterval(() => {
      if (window.L) {
        setLeafletReady(true);
        clearInterval(id);
        return;
      }
      if (++t2 > 30) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, []);
  React.useEffect(() => {
    if (!leafletReady || !wrapRef.current) return;
    const L = window.L;
    const el = wrapRef.current;
    const map = L.map(el, {
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    }).setView([54.4, -3], 5);
    el.classList.add("cf-atlas-leaflet");
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png", {
      attribution: "\xA9 OpenStreetMap \xB7 \xA9 CartoDB",
      subdomains: "abcd",
      maxZoom: 18
    }).addTo(map);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png", {
      attribution: "",
      subdomains: "abcd",
      maxZoom: 18,
      opacity: 0.7
    }).addTo(map);
    const popupHtml = (it) => {
      const accent = it.accent ? `var(--${it.accent})` : "var(--stone)";
      const photo = it.photos && it.photos[0] || it.photo || "";
      return `
        <div class="cf-pop" style="display:flex;gap:14px;align-items:flex-start;cursor:pointer">
          <div style="width:64px;height:64px;background:var(--surface-2);overflow:hidden;flex-shrink:0;border:1px solid var(--line)">
            ${photo ? `<img src="${photo}" alt="" style="width:100%;height:100%;object-fit:cover;filter:grayscale(0.45) contrast(1.05)"/>` : ""}
          </div>
          <div style="flex:1;min-width:0">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
              <span style="width:8px;height:8px;background:${accent};display:inline-block"></span>
              <span style="font-size:9px;letter-spacing:0.22em;font-weight:500;color:var(--stone)">${(it.landmark || it.type).toUpperCase()}</span>
            </div>
            <div style="font-weight:600;font-size:18px;letter-spacing:-0.005em">${it.name}</div>
            <div style="font-size:11px;color:var(--stone);margin-top:4px;font-variant-numeric:tabular-nums;letter-spacing:0.06em">${it.coords.lat.toFixed(3)}\xB0${it.coords.ns} \xB7 ${Math.abs(it.coords.lng).toFixed(3)}\xB0${it.coords.ew}</div>
            <div style="margin-top:10px;font-size:11px;letter-spacing:0.22em;font-weight:500;color:var(--ink);border-bottom:1px solid var(--ink);display:inline-block;padding-bottom:1px">VIEW MODEL \u2192</div>
          </div>
        </div>
      `;
    };
    const markers = [];
    items.forEach((it) => {
      const lat = it.coords.ns === "S" ? -it.coords.lat : it.coords.lat;
      const lng = it.coords.ew === "W" ? -it.coords.lng : it.coords.lng;
      const accent = it.accent ? `var(--${it.accent})` : "var(--ink)";
      const html = `
        <div class="cf-pin" style="color:${accent}">
          <div class="cf-pin-ring"></div>
          <div class="cf-pin-dot ${it.accent ? "" : "hollow"}" style="background:${it.accent ? accent : "var(--bone)"}"></div>
        </div>
      `;
      const icon = L.divIcon({
        className: "cf-pin-wrap",
        html,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      const m = L.marker([lat, lng], { icon }).addTo(map);
      const pop = L.popup({
        offset: [0, -6],
        closeOnClick: false,
        autoClose: false,
        closeButton: false,
        className: "cf-popup",
        maxWidth: 320
      }).setContent(popupHtml(it));
      m.on("mouseover", () => {
        m.bindPopup(pop).openPopup();
      });
      m.on("mouseout", () => {
        setTimeout(() => m.closePopup(), 1500);
      });
      m.on("click", () => {
        goRef.current("product", it.id);
      });
      markers.push(m);
    });
    try {
      const grp = L.featureGroup(markers);
      map.fitBounds(grp.getBounds().pad(0.25));
    } catch (e) {
    }
    map.on("click", () => map.scrollWheelZoom.enable());
    map.on("mouseout", () => map.scrollWheelZoom.disable());
    return () => {
      map.remove();
    };
  }, [leafletReady]);
  return /* @__PURE__ */ React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 360px", gap: 32, alignItems: "flex-start" } }, /* @__PURE__ */ React.createElement("div", { style: {
    border: "1px solid var(--ink)",
    background: "#ECEFEB",
    position: "relative",
    overflow: "hidden",
    height: 760
  } }, /* @__PURE__ */ React.createElement("div", { ref: wrapRef, style: { position: "absolute", inset: 0 } }), !leafletReady && /* @__PURE__ */ React.createElement("div", { style: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" } }, /* @__PURE__ */ React.createElement("div", { className: "eyebrow", style: { color: "var(--stone)" } }, "LOADING ORDNANCE MAP\u2026")), /* @__PURE__ */ React.createElement(CornerMarks, null), /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    top: 24,
    left: 24,
    background: "var(--bone)",
    border: "1px solid var(--ink)",
    padding: "14px 18px",
    maxWidth: 300,
    zIndex: 600
  } }, /* @__PURE__ */ React.createElement("div", { className: "eyebrow" }, "FIGURE 01 \xB7 ATLAS"), /* @__PURE__ */ React.createElement("div", { className: "h3", style: { marginTop: 6, fontSize: 18, fontWeight: 600 } }, "Every cityform we've made."), /* @__PURE__ */ React.createElement("div", { className: "tnum", style: { marginTop: 6, fontSize: 10.5, color: "var(--stone)" } }, "n = ", items.length, " \xB7 UK \xB7 1:11000 each")), /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    bottom: 24,
    right: 60,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    background: "var(--bone)",
    border: "1px solid var(--ink)",
    padding: "10px 14px",
    zIndex: 600
  } }, /* @__PURE__ */ React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 0, marginBottom: 6 } }, [0, 1, 2, 3].map((i) => /* @__PURE__ */ React.createElement("span", { key: i, style: { width: 24, height: 6, background: i % 2 ? "var(--ink)" : "var(--bone)", border: "1px solid var(--ink)" } }))), /* @__PURE__ */ React.createElement("span", { className: "tnum", style: { fontSize: 9, letterSpacing: "0.18em", fontWeight: 500 } }, "0\xA0\xA0\xA0100\xA0\xA0\xA0200 KM")), /* @__PURE__ */ React.createElement("div", { style: { position: "absolute", top: 24, right: 60, zIndex: 600 } }, /* @__PURE__ */ React.createElement(NorthMark, { size: 32 }))), /* @__PURE__ */ React.createElement("div", { style: { position: "sticky", top: 152 } }, /* @__PURE__ */ React.createElement("div", { className: "eyebrow" }, "Pin a place"), /* @__PURE__ */ React.createElement("h3", { className: "display-section", style: { fontSize: 38, marginTop: 14 } }, "The Isles, charted by what we've cut."), /* @__PURE__ */ React.createElement("p", { className: "body", style: { fontSize: 14.5, color: "var(--stone)", lineHeight: 1.6, marginTop: 14 } }, "Filled squares ship from the collection. Hollow squares are commissions or in development. Hover to see a place; click to open its city."), /* @__PURE__ */ React.createElement("button", { className: "btn-v2-ghost btn-v2", style: { marginTop: 24 }, onClick: () => go("atelier") }, "Suggest a new place\xA0", /* @__PURE__ */ React.createElement("span", { className: "btn-arrow" }, Icon.plus)), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 40, paddingTop: 20, borderTop: "1px solid var(--line)" } }, /* @__PURE__ */ React.createElement("div", { className: "eyebrow", style: { marginBottom: 14 } }, "Signature index"), /* @__PURE__ */ React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 18px" } }, Object.entries(window.ACCENTS).map(([k, v]) => /* @__PURE__ */ React.createElement("div", { key: k, style: { display: "flex", alignItems: "center", gap: 10, fontSize: 12 } }, /* @__PURE__ */ React.createElement("span", { style: { width: 12, height: 12, background: v.hex } }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { style: { fontWeight: 500 } }, v.name), /* @__PURE__ */ React.createElement("div", { style: { fontSize: 10, color: "var(--stone)", marginTop: 2 } }, v.note)))))), /* @__PURE__ */ React.createElement("div", { style: { marginTop: 32, paddingTop: 20, borderTop: "1px solid var(--line)" } }, /* @__PURE__ */ React.createElement("div", { className: "eyebrow", style: { marginBottom: 10 } }, "Sources"), /* @__PURE__ */ React.createElement("div", { style: { fontSize: 11.5, color: "var(--stone)", lineHeight: 1.6 } }, "Base layer: \xA9 OpenStreetMap, styled by CartoDB.", /* @__PURE__ */ React.createElement("br", null), "Each model: EA LIDAR (OGL v3) + OSM (ODbL)."))));
}
Object.assign(window, {
  useReveal,
  Reveal,
  RisingText,
  useScrollProgress,
  useParallax,
  OsContourBg,
  CornerMarks,
  NorthMark,
  TopNav,
  Footer,
  ProductCard,
  AtlasMap
});
window.AtlasMap = AtlasMap;
window.CornerMarks = CornerMarks;
window.Footer = Footer;
window.NorthMark = NorthMark;
window.OsContourBg = OsContourBg;
window.ProductCard = ProductCard;
window.Reveal = Reveal;
window.RisingText = RisingText;
window.TopNav = TopNav;
})();
